// src/components/SEOHelmet.tsx
import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { CLOUDFLARE_DOMAIN_ENDING } from '../constants'


// Interface for SEO props
interface SEOHelmetProps {
  title: string
  description?: string
  keywords?: string[]
  canonicalUrl?: string

  // Open Graph
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string

  // Twitter
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string

  // Additional SEO properties
  lang?: string
  robots?: string
  author?: string
  structuredData?: object
  // Hreflang for internationalization
  hreflangs?: Array<{ lang: string, url: string }>
  // Mobile specific
  viewport?: string
  themeColor?: string
}

export const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  lang = 'en',
  robots,
  author,
  structuredData,
  hreflangs,
  viewport = 'width=device-width, initial-scale=1',
  themeColor = '#ffffff'
}) => {
  const [finalRobots, setFinalRobots] = useState<string | undefined>(robots)

  useEffect(() => {
    const isPagesDevDomain = window.location.hostname.endsWith(CLOUDFLARE_DOMAIN_ENDING)

    if (isPagesDevDomain) {
      // Force noindex, nofollow for .pages.dev domains regardless of props
      setFinalRobots('noindex, nofollow')
    } else {
      // Use the provided robots value for custom domains
      setFinalRobots(robots)
    }
  }, [robots])

  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{title}</title>
      <html lang={lang} />
      <meta name="viewport" content={viewport} />
      {description && <meta name="description" content={description} />}
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {finalRobots && <meta name="robots" content={finalRobots} />}
      {author && <meta name="author" content={author} />}
      <meta name="theme-color" content={themeColor} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Hreflang tags for internationalization */}
      {hreflangs && hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description || ''} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
      <meta name="twitter:description" content={twitterDescription || ogDescription || description || ''} />
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}

      {/* Structured Data for rich snippets */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  )
}

export { SEOHelmetProps }
export default SEOHelmet
